// ArChat Incoming Message Sound Engine
// Created: 04-04-2026 12:18:00
// Author: SIVOL® - Aldo Frias - Argentina

import {
  normalizeConversationId,
  normalizeIncomingPayload,
  selectLoopConversationId,
  shouldMarkConversationSeen,
} from './incomingMessageSoundRules';
import {
  createIncomingMessageSoundState,
  trimProcessedMessageIds,
} from './incomingMessageSoundState';
import {
  buildIncomingMessageNotificationPayload,
  shouldOfferSystemIncomingNotification,
  showLocalIncomingMessageNotification,
} from '../../utils/systemIncomingNotification';

/** Recordatorio de beep mientras haya conversación pendiente (no abierta); 8–10 s pedido producto → 9 s. */
const LOOP_INTERVAL_MS = 9000;
// NO usar pointerdown/touchstart: se disparan antes del click que abre la conversación y
// tryUnlockAudio habilita audio mientras el pendiente IMS aún no fue limpiado → play residual al abrir.
// click/keydown llegan después del handler del contacto cuando emitimos conversation_seen (useContactsView).
const KEYDOWN_UNLOCK_OPTS = { passive: true };

function isLocalRuntime() {
  if (typeof window === 'undefined') return false;
  const host = String(window.location?.hostname ?? '').toLowerCase();
  return host === 'localhost' || host === '127.0.0.1' || host === '::1';
}

function shouldExposeImsDevBridge() {
  if (typeof window === 'undefined') return false;
  // En Vite dev siempre disponible. En runtime local (localhost), también.
  return import.meta.env.DEV || isLocalRuntime();
}

function shouldLogIms() {
  return import.meta.env.DEV || isLocalRuntime();
}

/**
 * @param {string} label
 * @param {object} [data]
 */
function devLog(label, data = undefined) {
  if (!shouldLogIms()) return;
  if (data === undefined) {
    console.log(label);
    return;
  }
  console.log(label, data);
}

class IncomingMessageSoundEngine {
  constructor() {
    this.state = createIncomingMessageSoundState();
    this.currentUserId = '';
    this.audioContext = null;
    this.unlockBound = false;
    this._onVisibilityFocus = null;
    this.onLoopTick = this.onLoopTick.bind(this);
    this.tryUnlockAudio = this.tryUnlockAudio.bind(this);
    this.installDevTools();
    this.bindVisibilityListeners();
  }

  bootstrapDevBridge() {
    this.installDevTools();
  }

  installDevTools() {
    if (!shouldExposeImsDevBridge()) return;
    window.__IMS_DEV__ = {
      ping: async () => {
        try {
          await this.tryUnlockAudio();
          await this.playHarmonicPing();
          devLog('[IMS_PLAY_OK]', { source: 'ims_dev_ping' });
          return { ok: true };
        } catch (error) {
          devLog('[IMS_PLAY_FAIL]', {
            source: 'ims_dev_ping',
            message: String(error?.message ?? error ?? ''),
          });
          return { ok: false, error: String(error?.message ?? error ?? 'unknown') };
        }
      },
      simulateIncoming: (payload = {}) => {
        const now = Date.now();
        this.notifyIncomingMessage({
          conversation_id: payload.conversationId ?? 'ims-dev-conv',
          message_id: payload.messageId ?? `ims-dev-${now}`,
          sender_id: payload.senderId ?? 'ims-dev-sender',
          text: payload.text ?? 'IMS DEV MESSAGE',
        });
      },
      stopAll: (reason = 'ims_dev_stop') => this.stopAll(reason),
      state: () => ({
        enabled: this.state.enabled,
        activeConversationId: this.state.activeConversationId,
        currentLoopConversationId: this.state.currentLoopConversationId,
        pendingCount: this.state.pendingByConversation.size,
        hasAutoplayBlock: this.state.hasAutoplayBlock,
      }),
    };
    if (typeof console !== 'undefined') {
      console.log('[IMS_DEV_BRIDGE_READY]', {
        mode: import.meta.env.MODE,
        dev: import.meta.env.DEV === true,
        localRuntime: isLocalRuntime(),
      });
    }
    devLog('[IMS_DEV_BRIDGE_READY]', {
      mode: import.meta.env.MODE,
      dev: import.meta.env.DEV === true,
      localRuntime: isLocalRuntime(),
    });
  }

  /**
   * @param {string|number|null|undefined} userId
   */
  setCurrentUserId(userId) {
    this.currentUserId = String(userId ?? '').trim();
  }

  /**
   * @param {boolean} enabled
   */
  setSoundEnabled(enabled) {
    const next = enabled === true;
    this.state.enabled = next;
    devLog('[IMS_RULE_RESULT]', { step: 'setSoundEnabled', enabled: next });
    if (!next) {
      devLog('[IMS_SKIP_DISABLED]', { reason: 'toggle_off', stopAll: true });
      this.stopAll('disabled');
      return;
    }
    // Pre-arm unlock path on first user gesture to avoid silent first attempt.
    this.bindUnlockListeners();
  }

  /**
   * @param {string|number|null|undefined} conversationId
   */
  setActiveConversationId(conversationId) {
    const prev = this.state.activeConversationId;
    this.state.activeConversationId = normalizeConversationId(conversationId);
    this.ensureVisibilityListeners();
    if (this.state.activeConversationId && this.state.activeConversationId !== prev) {
      this.clearPendingIfViewing();
    }
  }

  ensureVisibilityListeners() {
    if (this._onVisibilityFocus) return;
    this.bindVisibilityListeners();
  }

  /**
   * @param {string} conversationId
   * @returns {boolean}
   */
  isUserViewingConversation(conversationId) {
    return (
      this.isActiveConversation(conversationId) &&
      typeof document !== 'undefined' &&
      document.visibilityState === 'visible' &&
      document.hasFocus()
    );
  }

  clearPendingIfViewing() {
    if (!this.state.activeConversationId) return;
    const removed = this.state.pendingByConversation.delete(this.state.activeConversationId);
    if (removed) {
      devLog('[NO_BEEP_ACTIVE_CHAT]', {
        conversationId: this.state.activeConversationId,
        reason: 'active_conv_set',
      });
      this.reconcileLoopTarget();
    }
  }

  bindVisibilityListeners() {
    if (typeof document === 'undefined') return;
    this._onVisibilityFocus = () => this.clearPendingIfViewing();
    document.addEventListener('visibilitychange', this._onVisibilityFocus);
    window.addEventListener('focus', this._onVisibilityFocus);
  }

  unbindVisibilityListeners() {
    if (!this._onVisibilityFocus) return;
    document.removeEventListener('visibilitychange', this._onVisibilityFocus);
    window.removeEventListener('focus', this._onVisibilityFocus);
    this._onVisibilityFocus = null;
  }

  /**
   * Resuelve si la conversación indicada es la activa.
   * `window.__ACTIVE_CHAT_ID__` se actualiza sincrónicamente en el setter de
   * MessagesContext y es la fuente de verdad primaria. Si vale null/vacío,
   * no hay conversación abierta (contacts view) y retornamos false de inmediato.
   * `this.state.activeConversationId` se actualiza vía useEffect (un ciclo de
   * retraso) y solo se usa como fallback en entornos sin window.
   * @param {string} conversationId
   * @returns {boolean}
   */
  isActiveConversation(conversationId) {
    if (!conversationId) return false;
    if (typeof window !== 'undefined') {
      const globalCid = window.__ACTIVE_CHAT_ID__;
      if (globalCid == null || String(globalCid).trim() === '') return false;
      return String(globalCid).trim() === conversationId;
    }
    return this.state.activeConversationId !== '' && conversationId === this.state.activeConversationId;
  }

  /**
   * @param {object|null|undefined} rawMessage
   */
  notifyIncomingMessage(rawMessage) {
    const payload = normalizeIncomingPayload(rawMessage);
    if (!payload) {
      devLog('[BEEP_TRACE_EVENT]', { result: 'DROP_INVALID_PAYLOAD' });
      return;
    }

    const isActiveConv = this.isActiveConversation(payload.conversationId);
    const isSelf =
      !!this.currentUserId &&
      !!payload.senderId &&
      String(payload.senderId) === String(this.currentUserId);

    devLog('[BEEP_TRACE_EVENT]', {
      messageId: payload.messageId,
      conversationId: payload.conversationId,
      senderId: payload.senderId,
      currentUserId: this.currentUserId || null,
      engineActiveConvId: this.state.activeConversationId || null,
      windowActiveConvId: (typeof window !== 'undefined' && window.__ACTIVE_CHAT_ID__) || null,
      isSelf,
      isActiveConv,
      enabled: this.state.enabled,
    });

    if (!this.state.enabled) {
      devLog('[BEEP_TRACE_DECISION]', { messageId: payload.messageId, result: 'BLOCK_DISABLED' });
      return;
    }

    if (isSelf) {
      devLog('[BEEP_BLOCK_SELF_MESSAGE]', { messageId: payload.messageId, senderId: payload.senderId });
      return;
    }

    if (this.state.processedMessageIds.has(payload.messageId)) {
      devLog('[BEEP_BLOCK_DUPLICATE]', { messageId: payload.messageId });
      return;
    }

    this.state.processedMessageIds.add(payload.messageId);
    trimProcessedMessageIds(this.state.processedMessageIds);

    if (isActiveConv) {
      devLog('[BEEP_BLOCK_ACTIVE_CONV]', {
        messageId: payload.messageId,
        conversationId: payload.conversationId,
        engineCid: this.state.activeConversationId,
        windowCid: (typeof window !== 'undefined' && window.__ACTIVE_CHAT_ID__) || null,
      });
      return;
    }

    this.state.pendingByConversation.set(payload.conversationId, {
      conversationId: payload.conversationId,
      messageId: payload.messageId,
      ts: payload.ts,
    });
    devLog('[BEEP_ALLOW]', {
      messageId: payload.messageId,
      conversationId: payload.conversationId,
      reason: 'different_conversation_or_contacts_view',
      pendingCount: this.state.pendingByConversation.size,
    });

    if (shouldOfferSystemIncomingNotification()) {
      const built = buildIncomingMessageNotificationPayload(rawMessage);
      void showLocalIncomingMessageNotification({
        conversationId: built.conversationId || payload.conversationId,
        messageId: built.messageId || payload.messageId,
        title: built.title,
        body: built.body,
      });
    }

    this.ensureLoopRunning();
  }

  /**
   * @param {string|number|null|undefined} conversationId
   * @param {{ interacted?: boolean, force?: boolean }} [options]
   * @returns {boolean}
   */
  markConversationSeen(conversationId, options = {}) {
    const cid = normalizeConversationId(conversationId);
    if (!cid) return false;

    const visible =
      typeof document === 'undefined' ? true : document.visibilityState === 'visible';
    const focused =
      typeof document === 'undefined' || typeof document.hasFocus !== 'function'
        ? true
        : document.hasFocus();

    const ok = shouldMarkConversationSeen({
      conversationId: cid,
      activeConversationId: this.state.activeConversationId,
      isVisible: visible,
      isFocused: focused,
      interacted: options.interacted === true,
      force: options.force === true,
    });

    if (!ok) return false;

    const removed = this.state.pendingByConversation.delete(cid);
    if (removed) {
      devLog('[IMS_STOP_SEEN]', { conversationId: cid });
      devLog('[IMS_STOP_REASON]', { reason: 'seen', conversationId: cid });
      this.reconcileLoopTarget();
    }
    return removed;
  }

  /**
   * @param {string} [reason]
   */
  stopAll(reason = 'manual') {
    this.state.pendingByConversation.clear();
    if (this.state.loopTimerId != null) {
      clearInterval(this.state.loopTimerId);
      this.state.loopTimerId = null;
    }
    this.state.currentLoopConversationId = '';
    this.state.hasAutoplayBlock = false;
    this.unbindUnlockListeners();
    if (reason === 'provider_unmount') {
      this.unbindVisibilityListeners();
    }
    devLog('[IMS_STOP_REASON]', { reason });
    devLog('[IMS_STOP_ALL]', { reason });
  }

  hasPending() {
    return this.state.pendingByConversation.size > 0;
  }

  /**
   * React StrictMode desmonta el provider temporalmente: no usar stopAll() ahí
   * (borraría pendientes y silenciaría el recordatorio). Solo detiene el interval.
   */
  pauseLoopForProviderUnmount() {
    if (this.state.loopTimerId != null) {
      clearInterval(this.state.loopTimerId);
      this.state.loopTimerId = null;
    }
    devLog('[IMS_LOOP_PAUSE]', { reason: 'provider_unmount' });
  }

  /**
   * Tras remount del provider o si quedaron pendientes con el timer detenido.
   */
  resumeLoopIfPendingAfterMount() {
    if (!this.state.enabled) return;
    if (!this.hasPending()) return;
    this.ensureLoopRunning();
  }

  ensureLoopRunning() {
    this.reconcileLoopTarget();
    if (this.state.loopTimerId != null) return;
    this.state.loopTimerId = setInterval(this.onLoopTick, LOOP_INTERVAL_MS);
    devLog('[IMS_LOOP_START]', { conversationId: this.state.currentLoopConversationId });
    devLog('[IMS_START]', { conversationId: this.state.currentLoopConversationId });
    void this.onLoopTick();
  }

  reconcileLoopTarget() {
    const nextCid = selectLoopConversationId(this.state);
    this.state.currentLoopConversationId = nextCid;
    if (!nextCid && this.state.loopTimerId != null) {
      clearInterval(this.state.loopTimerId);
      this.state.loopTimerId = null;
    }
  }

  async onLoopTick() {
    if (!this.state.enabled) return;
    if (!this.state.currentLoopConversationId) return;

    if (this.isActiveConversation(this.state.currentLoopConversationId)) {
      devLog('[BEEP_BLOCK_ACTIVE_CONV]', {
        conversationId: this.state.currentLoopConversationId,
        source: 'loop_tick',
        engineCid: this.state.activeConversationId,
        windowCid: (typeof window !== 'undefined' && window.__ACTIVE_CHAT_ID__) || null,
      });
      this.state.pendingByConversation.delete(this.state.currentLoopConversationId);
      this.reconcileLoopTarget();
      return;
    }

    devLog('[BEEP_TRACE_DECISION]', {
      result: 'PLAY',
      conversationId: this.state.currentLoopConversationId,
      engineCid: this.state.activeConversationId || null,
      windowCid: (typeof window !== 'undefined' && window.__ACTIVE_CHAT_ID__) || null,
      hasFocus: typeof document !== 'undefined' && document.hasFocus(),
      visible: typeof document !== 'undefined' && document.visibilityState === 'visible',
    });
    try {
      await this.playHarmonicPing();
    } catch (error) {
      const message = String(error?.message ?? error ?? '');
      const blocked =
        message.toLowerCase().includes('notallowederror') ||
        message.toLowerCase().includes('user gesture') ||
        message.toLowerCase().includes('interact');
      if (blocked) {
        this.state.hasAutoplayBlock = true;
        this.bindUnlockListeners();
      }
    }
  }

  bindUnlockListeners() {
    if (this.unlockBound || typeof window === 'undefined') return;
    this.unlockBound = true;
    window.addEventListener('click', this.tryUnlockAudio);
    window.addEventListener('keydown', this.tryUnlockAudio, KEYDOWN_UNLOCK_OPTS);
  }

  unbindUnlockListeners() {
    if (!this.unlockBound || typeof window === 'undefined') return;
    this.unlockBound = false;
    window.removeEventListener('click', this.tryUnlockAudio);
    window.removeEventListener('keydown', this.tryUnlockAudio, KEYDOWN_UNLOCK_OPTS);
  }

  async tryUnlockAudio(event) {
    try {
      devLog('[IMS_UNLOCK_GESTURE]', { type: event?.type ?? 'synthetic' });
      await this.ensureAudioContext();
      if (this.audioContext?.state === 'suspended') {
        await this.audioContext.resume();
      }
      const unlocked = this.audioContext?.state === 'running';
      this.state.hasAutoplayBlock = !unlocked;
      this.state.lastUnlockAt = Date.now();
      devLog('[IMS_UNLOCK_RESULT]', { unlocked, ctxState: this.audioContext?.state });
      if (!unlocked) return;
      this.unbindUnlockListeners();
      if (this.state.enabled && this.hasPending()) {
        devLog('[IMS_UNLOCK_PLAY_PENDING]', { pendingCount: this.state.pendingByConversation.size });
        void this.onLoopTick();
      }
    } catch (_) {
      // noop
    }
  }

  async ensureAudioContext() {
    if (this.audioContext) return this.audioContext;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) throw new Error('AudioContextUnavailable');
    this.audioContext = new Ctx();
    return this.audioContext;
  }

  /**
   * Ping suave de dos notas breves.
   * @returns {Promise<void>}
   */
  async playHarmonicPing() {
    if (typeof window === 'undefined') return;
    const ctx = await this.ensureAudioContext();
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }
    if (ctx.state !== 'running') {
      throw new Error('NotAllowedError: AudioContext not running after resume (autoplay policy)');
    }
    const now = ctx.currentTime;
    const gain = ctx.createGain();
    gain.gain.value = 0.0001;
    gain.connect(ctx.destination);

    const oscA = ctx.createOscillator();
    const oscB = ctx.createOscillator();
    oscA.type = 'sine';
    oscB.type = 'triangle';
    oscA.frequency.setValueAtTime(523.25, now); // C5
    oscB.frequency.setValueAtTime(659.25, now + 0.08); // E5

    oscA.connect(gain);
    oscB.connect(gain);

    // Ganancia más audible sin ser agresiva.
    gain.gain.exponentialRampToValueAtTime(0.16, now + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.09, now + 0.11);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.34);

    oscA.start(now);
    oscA.stop(now + 0.24);
    oscB.start(now + 0.08);
    oscB.stop(now + 0.34);
  }
}

export const incomingMessageSoundEngine = new IncomingMessageSoundEngine();

